import {useTestRoom} from "../../../../../hooks/useTestRoom";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Accordion, AccordionDetails, AccordionGroup, AccordionSummary, Divider, Grid, Stack, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {useBreakpoint} from "../../../../../hooks/useBreakPoint";
import {Question, QuestionGroup, QuestionType, RawQuestion} from "../../../../../models/question";
import IeltsMatchingQuestionComponent from "../question/IeltsMatchingQuestionComponent";
import IeltsMultipleChoiceQuestionComponent from "../question/IeltsMultipleChoiceQuestionComponent";
import IeltsOpenAnswerQuestionComponent from "../question/IeltsOpenAnswerQuestionComponent";
import IeltsTrueFalseQuestionComponent from "../question/IeltsTrueFalseQuestionComponent";

const IeltsReadingTestRoomComponent = () => {
    const testRoom = useTestRoom()
    const [questionSet, setQuestionSet] = useState<QuestionGroup>()
    const {t} = useTranslation()
    // On small screens the test card is the only scroll area; the passage folds away instead of scrolling on its own.
    const compact = useBreakpoint().isBelow("md")

    const passage = <Typography textAlign={"justify"} level={"body-sm"} textColor={"text.tertiary"}>
        <i>{questionSet?.content}</i>
    </Typography>

    const QuestionComponent = (props: { question: RawQuestion, questionGroupIndex: number }) => {
        switch (props.question.type) {
            case "matching":
                return <IeltsMatchingQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                       question={props.question} questionId={questionSet?.id}
                                                       testId={questionSet?.testId}
                                                       sectionName={questionSet?.sectionName}
                                                       subSectionNumber={questionSet?.subSectionNumber}/>
            case "multiple_choice":
                return <IeltsMultipleChoiceQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                             question={props.question} parentQuestion={questionSet}/>
            case "fill_blank":
                return <IeltsOpenAnswerQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                         question={props.question} questionId={questionSet?.id}
                                                         testId={questionSet?.testId}
                                                         sectionName={questionSet?.sectionName}
                                                         subSectionNumber={questionSet?.subSectionNumber}/>
            case "short_answer":
                return <IeltsOpenAnswerQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                         testId={questionSet?.testId}
                                                         sectionName={questionSet?.sectionName}
                                                         question={props.question} questionId={questionSet?.id}
                                                         subSectionNumber={questionSet?.subSectionNumber}/>

            case "true_false_not_given":
                return <IeltsTrueFalseQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                        question={props.question} questionId={questionSet?.id}
                                                        testId={questionSet?.testId}
                                                        sectionName={questionSet?.sectionName}
                                                        subSectionNumber={questionSet?.subSectionNumber}/>
        }
    }

    useEffect(() => {
        setQuestionSet(testRoom.questions?.find(value => value.subSectionNumber === testRoom.currentSubsectionNumber) as QuestionGroup)
    }, [testRoom.currentSubsectionNumber, testRoom.questions])

    return <Grid container spacing={4} sx={{overflowY: {md: "hidden"}, height: {md: "100%"}}}>
        <Grid xs={12} md={6}>
            <Stack gap={3}>
                <Typography level={"title-lg"}>
                    {questionSet?.instruction}
                </Typography>

                {compact ?
                    // Keyed by passage so each new passage starts expanded.
                    <AccordionGroup key={questionSet?.id ?? questionSet?.subSectionNumber} variant={"outlined"}
                                    sx={{borderRadius: "md"}}>
                        <Accordion defaultExpanded>
                            <AccordionSummary>
                                <Typography level={"title-sm"}>{t("readingPassageLabel")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>{passage}</AccordionDetails>
                        </Accordion>
                    </AccordionGroup> :
                    passage}
            </Stack>
        </Grid>

        <Grid xs={12} md={6} height={{md: "100%"}} sx={{overflowY: null, overflowX: {md: "hidden"}}}>
            <Stack gap={4} boxSizing={"border-box"}>
                {questionSet?.questions.map((question, index) =>
                    <QuestionComponent questionGroupIndex={index}
                                       question={question}/>)}
            </Stack>
        </Grid>

    </Grid>
}

export default IeltsReadingTestRoomComponent